import React, { useEffect, useRef, useState } from "react";
import { BACKEND_URL } from "./../backendconfig";
import "./CategoryManager.css";

const api = `${BACKEND_URL.replace(/\/+$/, "")}/category-manager.php`;

async function readResponse(response) {
  let data;

  try {
    data = await response.json();
  } catch {
    throw new Error("The API did not return JSON. Check the PHP file.");
  }

  if (!response.ok || !data.ok) {
    throw new Error(data.message || "Unable to process request.");
  }

  return data;
}

export default function CategoryManager() {
  const [categories, setCategories] = useState([]);
  const [counts, setCounts] = useState({});
  const [categoryVersion, setCategoryVersion] = useState("");
  const [courseVersion, setCourseVersion] = useState("");
  const [csrf, setCsrf] = useState("");

  const [newName, setNewName] = useState("");
  const [names, setNames] = useState({});
  const [replacements, setReplacements] = useState({});

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const requestBusy = useRef(false);

  function applyData(data) {
    setCategories(data.categories);
    setCounts(data.counts);
    setCategoryVersion(data.categoryVersion);
    setCourseVersion(data.courseVersion);
    setCsrf(data.csrf);
    setNames(Object.fromEntries(data.categories.map((name) => [name, name])));
    setReplacements({});
  }

  useEffect(() => {
    const controller = new AbortController();

    async function load() {
      try {
        const response = await fetch(api, {
          credentials: "include",
          cache: "no-store",
          signal: controller.signal,
        });

        const data = await readResponse(response);

        if (!controller.signal.aborted) applyData(data);
      } catch (error) {
        if (!controller.signal.aborted) {
          setNotice({ type: "error", text: error.message });
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    load();

    return () => controller.abort();
  }, []);

  async function send(action, original = "", name = "", replacement = "") {
    if (requestBusy.current) return;

    if (["add", "rename"].includes(action) && !name.trim()) {
      setNotice({
        type: "error",
        text: "Please enter a category name.",
      });
      return;
    }

    if (action === "delete") {
      if ((counts[original] || 0) > 0 && !replacement) {
        setNotice({
          type: "error",
          text: "Select a replacement category before deleting.",
        });
        return;
      }

      if (
        !window.confirm(
          `Delete "${original}"? Assigned courses will move to the selected replacement.`,
        )
      ) {
        return;
      }
    }

    requestBusy.current = true;
    setBusy(true);
    setNotice(null);

    try {
      // Existing PHP reads $_POST, so send FormData.
      const body = new FormData();

      Object.entries({
        action,
        original,
        name: name.trim(),
        replacement,
        csrf,
        categoryVersion,
        courseVersion,
      }).forEach(([key, value]) => body.append(key, value));

      const response = await fetch(api, {
        method: "POST",
        credentials: "include",
        body,
      });

      const data = await readResponse(response);

      applyData(data);

      if (action === "add") setNewName("");

      setNotice({
        type: "success",
        text: data.message || "Categories updated successfully.",
      });
    } catch (error) {
      setNotice({ type: "error", text: error.message });
    } finally {
      requestBusy.current = false;
      setBusy(false);
    }
  }

  return (
    <main className="catm-page">
      <header className="catm-header">
        <div>
          <h1>Category Manager</h1>
          <p>
            Manage category names and order. Renaming updates assigned courses.
          </p>
        </div>

        <span className="catm-count">{categories.length} categories</span>
      </header>

      {notice && (
        <div
          className={`catm-notice catm-${notice.type}`}
          role={notice.type === "error" ? "alert" : "status"}
        >
          {notice.text}
        </div>
      )}

      {loading ? (
        <section className="catm-panel" role="status">
          Loading categories…
        </section>
      ) : !csrf ? (
        <section className="catm-panel">
          Unable to load categories. Check the API and reload.
        </section>
      ) : (
        <fieldset className="catm-fieldset" disabled={busy}>
          <section className="catm-panel">
            <h2>Add Category</h2>

            <form
              className="catm-add-form"
              onSubmit={(event) => {
                event.preventDefault();
                send("add", "", newName);
              }}
            >
              <label htmlFor="catm-new-name">Category Name</label>

              <div className="catm-add-row">
                <input
                  id="catm-new-name"
                  required
                  maxLength={100}
                  placeholder="Enter new category"
                  value={newName}
                  onChange={(event) => setNewName(event.target.value)}
                />

                <button type="submit" className="catm-primary">
                  <i className="bi bi-plus-lg" aria-hidden="true" />
                  Add Category
                </button>
              </div>
            </form>
          </section>

          <section className="catm-panel">
            <h2>All Categories</h2>

            <div className="catm-table-scroll">
              <table className="catm-table">
                <thead>
                  <tr>
                    <th scope="col">No.</th>
                    <th scope="col">Order</th>
                    <th scope="col">Category Name</th>
                    <th scope="col">Courses</th>
                    <th scope="col">Delete / Reassign</th>
                  </tr>
                </thead>

                <tbody>
                  {categories.map((name, index) => {
                    const count = counts[name] || 0;
                    const cannotDelete = count > 0 && categories.length < 2;

                    return (
                      <tr key={name}>
                        <td>{index + 1}</td>

                        <td>
                          <div className="catm-order">
                            <button
                              type="button"
                              title="Move up"
                              aria-label={`Move ${name} up`}
                              disabled={busy || index === 0}
                              onClick={() => send("up", name)}
                            >
                              <i
                                className="bi bi-arrow-up"
                                aria-hidden="true"
                              />
                            </button>

                            <button
                              type="button"
                              title="Move down"
                              aria-label={`Move ${name} down`}
                              disabled={busy || index === categories.length - 1}
                              onClick={() => send("down", name)}
                            >
                              <i
                                className="bi bi-arrow-down"
                                aria-hidden="true"
                              />
                            </button>
                          </div>
                        </td>

                        <td>
                          <form
                            className="catm-row-form"
                            onSubmit={(event) => {
                              event.preventDefault();
                              send("rename", name, names[name] ?? name);
                            }}
                          >
                            <input
                              required
                              maxLength={100}
                              aria-label={`Category name: ${name}`}
                              value={names[name] ?? name}
                              onChange={(event) =>
                                setNames((previous) => ({
                                  ...previous,
                                  [name]: event.target.value,
                                }))
                              }
                            />

                            <button
                              type="submit"
                              className="catm-save"
                              title="Save category name"
                              aria-label={`Save category name: ${name}`}
                            >
                              <i className="bi bi-check2" aria-hidden="true" />
                            </button>
                          </form>
                        </td>

                        <td>
                          <span className="catm-badge">{count}</span>
                        </td>

                        <td>
                          <form
                            className="catm-row-form"
                            onSubmit={(event) => {
                              event.preventDefault();
                              send(
                                "delete",
                                name,
                                "",
                                replacements[name] || "",
                              );
                            }}
                          >
                            {count > 0 && (
                              <select
                                required
                                aria-label={`Move courses from ${name} to`}
                                value={replacements[name] || ""}
                                onChange={(event) =>
                                  setReplacements((previous) => ({
                                    ...previous,
                                    [name]: event.target.value,
                                  }))
                                }
                              >
                                <option value="">Move courses to…</option>

                                {categories
                                  .filter((other) => other !== name)
                                  .map((other) => (
                                    <option key={other} value={other}>
                                      {other}
                                    </option>
                                  ))}
                              </select>
                            )}

                            <button
                              type="submit"
                              className="catm-danger"
                              title="Delete category"
                              aria-label={`Delete ${name}`}
                              disabled={busy || cannotDelete}
                            >
                              <i className="bi bi-trash" aria-hidden="true" />
                            </button>
                          </form>

                          {cannotDelete && (
                            <small>
                              Add another category before reassigning courses.
                            </small>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {categories.length === 0 && (
                    <tr>
                      <td colSpan={5} className="catm-empty">
                        No categories yet. Add your first category above.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </fieldset>
      )}
    </main>
  );
}
